import React, { useState, useRef } from 'react';
import { Alert, Button, Field, Input, Spinner } from '@billda/design-system';
import { useMagicCodeLoginStore } from '../../stores/magicCodeLoginStore';
import { useAuthStore } from '../../stores/authStore';

interface EmailStepProps {
  onSuccess: (email: string) => void;
}

const EmailStep: React.FC<EmailStepProps> = ({ onSuccess }) => {
  const {
    email,
    loading,
    error,
    setEmail,
    setError,
    setEmailSent,
    clearError,
    goToCodeStep,
  } = useMagicCodeLoginStore();

  const { requestMagicCode } = useAuthStore();
  const [emailError, setEmailError] = useState('');
  const submittingRef = useRef(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async () => {
    // Prevent double submission (e.g., from React StrictMode in development)
    if (submittingRef.current) return;

    // Perform synchronous validation outside try block to allow safe early returns
    clearError();
    setEmailError('');

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Set guard before async operation
    submittingRef.current = true;

    try {
      const result = await requestMagicCode(email);

      if (!result.success && result.error) {
        console.error('Magic code request error:', result.error);
        setError(result.error);
        return;
      }

      // Seed the resend cooldown so CodeStep picks it up on mount
      const expiry = Date.now() + 30 * 1000;
      sessionStorage.setItem(
        `magic-code-resend-expiry-${email}`,
        String(expiry)
      );

      setEmailSent(true);
      goToCodeStep();
      onSuccess(email);
    } finally {
      submittingRef.current = false;
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleEmailSubmit();
    }
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleEmailSubmit();
  };

  return (
    <div data-testid="magic-code-email-step">
      <form onSubmit={handleFormSubmit} noValidate>
        {error && (
          <Alert
            severity="error"
            className="mb-6"
            data-testid="magic-code-error-alert"
          >
            {error}
          </Alert>
        )}

        <div className="mb-6">
          <Field label="Email Address" error={emailError || undefined}>
            <Input
              name="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              invalid={!!emailError}
              disabled={loading}
              autoFocus
              autoComplete="username webauthn"
              inputSize="lg"
              className="text-[1.1rem]"
              data-testid="magic-code-email-input"
            />
          </Field>
        </div>

        <Button
          variant="neutral"
          fullWidth
          size="lg"
          className="mb-4 py-3 text-[1.1rem]"
          type="submit"
          disabled={loading || !email.trim()}
          data-testid="magic-code-send-button"
        >
          {loading ? (
            <Spinner
              size={24}
              color="inherit"
              data-testid="magic-code-email-loading"
            />
          ) : (
            'Login'
          )}
        </Button>

        {/* Traditional login has been removed */}
      </form>
    </div>
  );
};

export default EmailStep;
